// @ts-ignore
import css from "./widget.css";
import { openMenu } from "../menu/menu";
import { getCurrentDevice, DeviceType } from "@/utils/device";
import { Accessibility } from "@/icons/accessibility";
import { Assist } from "@/icons/assist";
import { DefaultIcon } from "@/icons/default";
import { wheel } from "@/icons/wheel";
import { pluginConfig } from "@/globals/pluginConfig";
import { IconItem } from "@/types/widget";
import { detectedLanguage } from "@/utils/getScriptDataAttribute";
import { LANGUAGE_DICTIONARY } from "@/i18n/Languages";
import { getDefaultLanguage } from "@/i18n/getDefaultLanguage";

/**
 * Icon library for the widget
 */
export const ICON_LIBRARY: readonly IconItem[] = [
  { id: "default", icon: DefaultIcon },
  { id: "accessibility", icon: Accessibility },
  { id: "assist", icon: Assist },
  { id: "wheel", icon: wheel },
] as const;

/**
 * Global widget element reference
 */
export let $widget: HTMLElement | null = null;

/**
 * Position styles configuration
 */
const POSITION_STYLES = {
  "bottom-right": (margins: any) => ({
    bottom: `${margins.bottom}px`,
    right: `${margins.right}px`,
    left: "auto",
  }),
  "top-left": (margins: any) => ({
    top: `${margins.top}px`,
    left: `${margins.left}px`,
    bottom: "auto",
  }),
  "top-right": (margins: any) => ({
    top: `${margins.top}px`,
    right: `${margins.right}px`,
    left: "auto",
    bottom: "auto",
  }),
  "bottom-left": (margins: any) => ({
    bottom: `${margins.bottom}px`,
    left: `${margins.left}px`,
  }),
} as const;

/**
 * Gets button styles based on current device and position
 * @returns CSS styles object
 */
function getButtonStyle(): Record<string, string> {
  const device = getCurrentDevice();
  const { position, margins } = pluginConfig;
  const currentPosition = position[device];
  const currentMargins = margins[device];

  const positionStyle =
    POSITION_STYLES[currentPosition as keyof typeof POSITION_STYLES];
  return positionStyle
    ? positionStyle(currentMargins)
    : POSITION_STYLES["bottom-left"](currentMargins);
}

/**
 * Handles keyboard navigation for the widget button
 * @param event - Keyboard event
 * @param button - The button element
 */
function handleWidgetKeyboardNavigation(event: KeyboardEvent, button: HTMLElement): void {
  switch (event.key) {
    case 'Enter':
    case ' ':
      event.preventDefault();
      openMenu();
      break;
    case 'Escape':
      // Close menu if open by finding it within the widget's shadow DOM
      const widgetContainer = document.querySelector('#cya11y-container');
      if (widgetContainer) {
        const shadowRoot = widgetContainer.shadowRoot;
        if (shadowRoot) {
          const menu = shadowRoot.querySelector('.cya11y-menu') as HTMLElement;
          if (menu && menu.style.display !== 'none') {
            const closeButton = menu.querySelector('.cya11y-menu-close') as HTMLElement;
            if (closeButton) {
              closeButton.click();
              // Update ARIA expanded state
              button.setAttribute('aria-expanded', 'false');
            }
          }
        }
      }
      break;
  }
}

/**
 * Renders the accessibility widget
 * @returns The widget element or null if not rendered
 */
export function renderWidget(): HTMLElement | null {
  try {
    const device = getCurrentDevice();

    // Check if widget should be displayed based on device status
    const deviceStatus =
      device === "mobile"
        ? pluginConfig.status.mobile
        : pluginConfig.status.desktop;

    if (!deviceStatus) {
      console.log(`Widget disabled for ${device} device`);
      return null;
    }

    // Create container and shadow DOM
    const $container: HTMLElement = document.createElement("div");
    $container.id = "cya11y-container";
    const shadow = $container.attachShadow({ mode: "open" });

    const currentPosition = pluginConfig.position[device];
    const selectedIcon = ICON_LIBRARY.find(
      (icon) => icon.id === pluginConfig.iconId
    );

    const defaultLanguage = getDefaultLanguage();
    // Create widget HTML with enhanced accessibility
    const label = LANGUAGE_DICTIONARY[defaultLanguage][pluginConfig.label];
    shadow.innerHTML = `
      <style id="cya11y-widget-css">${css}</style>
      <div class="cya11y-widget" data-position="${currentPosition}">
        <div class="cya11y-widget-icon">
          <div class="cya11y-tooltip" role="tooltip" aria-hidden="true">
            <div class="cya11y-tooltip-arrow"></div>
            ${label}
          </div>
          <button 
             class="cya11y-menu-btn" 
             aria-label="${label || 'Accessibility menu'}"
             aria-expanded="false"
             aria-haspopup="dialog"
             aria-controls="cya11y-accessibility-menu"
             role="button" 
             type="button"
             tabindex="0">
            <span class="cya11y-btn-content" aria-hidden="true">
              ${selectedIcon?.icon || DefaultIcon}
            </span>
            <span class="cya11y-sr-only">Open accessibility menu</span>
          </button>
        </div>
      </div>
    `;

    // Get widget element and apply styles
    $widget = shadow.querySelector(".cya11y-widget") as HTMLElement;
    if (!$widget) {
      throw new Error("Failed to find widget element");
    }

    $widget.style.setProperty(
      "--cya11y-primary-color",
      pluginConfig.primaryColor
    );
    $widget.style.setProperty("--cya11y-size", `${pluginConfig.iconSize}px`);

    const $btn = $widget.querySelector(".cya11y-menu-btn") as HTMLElement;
    const $icon = $widget.querySelector(".cya11y-widget-icon") as HTMLElement;

    if ($btn && $icon) {
      Object.assign($btn.style, getButtonStyle());
      Object.assign($icon.style, getButtonStyle());

      // Enhanced click event listener with accessibility improvements
      $btn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        openMenu();
        
        // Update ARIA expanded state after a short delay to ensure menu is rendered
        setTimeout(() => {
          const widgetContainer = document.querySelector('.cya11y-container');
          if (widgetContainer) {
            const widgetShadowRoot = widgetContainer.shadowRoot;
            if (widgetShadowRoot) {
              const menu = widgetShadowRoot.querySelector('.cya11y-menu') as HTMLElement;
              if (menu) {
                const isExpanded = menu.style.display !== 'none';
                $btn.setAttribute('aria-expanded', isExpanded.toString());
              }
            }
          }
        }, 10);
      });

      // Add keyboard navigation
      $btn.addEventListener("keydown", (event) => {
        handleWidgetKeyboardNavigation(event, $btn);
      });

      // Add focus management
      $btn.addEventListener("focus", () => {
        $btn.classList.add("cya11y-focus-visible");
      });

      $btn.addEventListener("blur", () => {
        $btn.classList.remove("cya11y-focus-visible");
      });
    }

    // Append to document
    document.body.insertBefore($container, document.body.firstChild);

    return $widget;
  } catch (error) {
    console.error("Failed to render widget:", error);
    return null;
  }
}
